// The one error every `http` call throws. Kept apart from client.ts so the
// Legacy-facing entry (AppProviders) can read it without loading openapi-fetch,
// which Jest 27 cannot parse.
import type { components } from "./schema";

export class AppApiError extends Error {
  readonly status: number;
  // The server's PascalCase code (`Forbidden`, `ValidationFailed`), or one the
  // client assigns: `Unauthorized` for the Legacy checkRole 401, `Unknown` for
  // another non-envelope body, `Network` for no response or no JSON.
  readonly code: string;
  readonly details?: unknown;

  constructor(status: number, code: string, message: string, details?: unknown) {
    super(message);
    this.name = "AppApiError";
    this.status = status;
    this.code = code;
    this.details = details;
  }
}

// One entry of a ValidationFailed's `details.issues`: which field of the
// request, and the server's code for it. `field` and `code` are strings the
// handler chooses; switch on `code`, never on `message`.
export type ValidationIssue =
  components["responses"]["ValidationFailed"]["content"]["application/json"]["error"]["details"]["issues"][number];

const isIssue = (value: unknown): value is ValidationIssue => {
  if (typeof value !== "object" || value === null) return false;
  const { field, code, message } = value as Record<string, unknown>;
  return typeof field === "string" && typeof code === "string" && typeof message === "string";
};

// The issues of a ValidationFailed, malformed entries dropped; null for any
// other error.
export function validationIssuesOf(error: unknown): ValidationIssue[] | null {
  if (!(error instanceof AppApiError) || error.code !== "ValidationFailed") return null;
  const issues = (error.details as { issues?: unknown } | null | undefined)?.issues;
  return Array.isArray(issues) ? issues.filter(isIssue) : [];
}
